import { useFormContext, useWatch } from "react-hook-form";

import { Card } from "src/base-components/Card";
import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { Switch } from "src/base-components/Switch";
import { CaseTemplateFormValues } from "src/cases/templates/formUtils/types";
import { useCheckFieldPermission } from "src/cases/templates/useCheckFieldPermission";
import { Accordion } from "src/design-system/Accordion";
import { useModal } from "src/design-system/Modal";

type FormToggleAccordionProps = {
  itemIndex: number;
  itemType: "task" | "action";
  accordionValue: string;
  children: React.ReactNode;
  onEnableAccordionItem: (value: string) => void;
  onDisableAccordionItem: (value: string) => void;
};

export const FormToggleAccordion: React.FC<FormToggleAccordionProps> = ({
  itemIndex,
  itemType,
  accordionValue,
  children,
  onEnableAccordionItem,
  onDisableAccordionItem,
}) => {
  const form = useFormContext<CaseTemplateFormValues>();
  const hasPermission = useCheckFieldPermission();

  const fieldPath = itemType === "task" ? "case_tasks" : "case_actions";
  const isFormEnabled = useWatch({
    name: `attributes.${fieldPath}.${itemIndex}.form`,
    compute: (value) => Array.isArray(value?.fields),
  });

  const {
    openModal: openFormDeleteModal,
    isOpen: isFormDeleteModalOpen,
    closeModal: closeFormDeleteModal,
  } = useModal();

  const toggleOffForm = () => {
    form.setValue(`attributes.${fieldPath}.${itemIndex}.form`, null);
    closeFormDeleteModal();
    onDisableAccordionItem(accordionValue);
  };

  const toggleOnForm = () => {
    form.setValue(`attributes.${fieldPath}.${itemIndex}.form`, {
      fields: [
        {
          type: "text" as const,
          key: "",
          display_name: "",
          required: false,
          description: "",
        },
      ],
    });
  };

  const onToggleForm = () => {
    if (isFormEnabled) {
      openFormDeleteModal();
    } else {
      onEnableAccordionItem(accordionValue);
      toggleOnForm();
    }
  };

  return (
    // We need to wrap the Accordion.Item in a div to avoid visual extra dividers
    <div>
      <Accordion.Item
        dataLoc="form-builder-accordion"
        isDisabled={!isFormEnabled}
        rightSlot={
          <Switch
            dataLoc="form-enabled-switch"
            disabled={
              !hasPermission(
                "edit",
                `attributes.${fieldPath}.${itemIndex}.form`,
              )
            }
            enabled={isFormEnabled}
            onChange={onToggleForm}
          />
        }
        label="Configure the fields reviewers should fill out"
        value={accordionValue}
      >
        {isFormEnabled && <Card className="w-full">{children}</Card>}
      </Accordion.Item>
      <ConfirmationModal
        cancelationButtonText="Cancel"
        confirmationButtonText="Delete"
        description="Are you sure you want to delete this form group? This action cannot be undone. All forms inside this group will be removed permanently."
        open={isFormDeleteModalOpen}
        title="Delete form group"
        variant="danger"
        onClose={closeFormDeleteModal}
        onConfirm={toggleOffForm}
      />
    </div>
  );
};
